import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, Subject } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { GroupsService } from 'src/app/views/groups/groups.service';
import { UserComponent } from '../ui/user/user.component';

@Component({
  selector: 'app-user-group-search',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TranslateModule, MatFormFieldModule, MatInputModule, MatIconModule, MatAutocompleteModule, MatButtonModule, UserComponent],
  template: `
    <div class="user-search-wrapper" [class.with-button]="mode === 'with-button'">
      <mat-form-field appearance="fill" class="full-width">
        <input matInput [formControl]="searchControl" [matAutocomplete]="auto" [type]="inputType" (input)="onInput($event)" [placeholder]="placeholder" [disabled]="disabled" />
        @if (iconPosition === 'prefix') {
          <mat-icon matPrefix>{{ icon }}</mat-icon>
        } @else {
          <mat-icon matSuffix>{{ icon }}</mat-icon>
        }
      </mat-form-field>
      <mat-autocomplete #auto="matAutocomplete" (optionSelected)="onOptionSelected($event)">
        @for (option of filteredOptions(); track option) {
          <mat-option [value]="option">
            <ui-user [name]="option.displayName" [imageUrl]="option.photo" [description]="option.email || option.class" size="sm" />
          </mat-option>
        }
      </mat-autocomplete>
      @if (mode === 'with-button') {
        <button mat-flat-button color="primary" class="invite-button" (click)="emitSelection()" [disabled]="!selectedUser()">
          <mat-icon>{{ buttonIcon }}</mat-icon>
          <span>{{ buttonText }}</span>
        </button>
      }
    </div>
  `,
  styleUrls: ['./user-group-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGroupSearchComponent implements OnInit, OnDestroy {
  @Input() searchControl = new FormControl('');
  @Input() placeholder = 'Search';
  @Input() inputType = 'search';
  @Input() icon = 'search';
  @Input() iconPosition: 'prefix' | 'suffix' = 'suffix';
  @Input() buttonIcon = 'add';
  @Input() buttonText = 'Invite';
  @Input() disabled = false;
  @Input() mode: 'standalone' | 'with-button' = 'standalone';

  @Output() buttonClick = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<any>();

  private authService = inject(AuthService);
  private groupService = inject(GroupsService);
  private storage = inject(Storage);

  public filteredOptions = signal<any[]>([]);
  public selectedUser = signal<any | null>(null);
  private searchInput$ = new Subject<string>();

  ngOnInit(): void {
    this.searchInput$.pipe(debounceTime(500)).subscribe((value) => {
      if (value) {
        this.groupService.findEntity(value).subscribe({
          next: async (results) => {
            for (const e of results) {
              e.photo = e.photo ? await this.getDownloadURL(e.photo) : 'assets/images/no_user.png';
              e.displayName = e.name || e.fullName;
              e.class = e.type === 'group' ? 'group' : 'user';
            }
            this.filteredOptions.set(results);
          },
          error: (err) => {
            console.error(err);
          },
        });
      } else {
        this.filteredOptions.set([]);
      }
    });
  }

  async getDownloadURL(url) {
    try {
      const reference = ref(this.storage, url);
      return await getDownloadURL(reference);
    } catch (error) {
      return 'assets/images/no_user.png';
    }
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
  }

  onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchInput$.next(value);
    this.selectedUser.set(null);
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const user = {
      ...event.option.value,
      role: 'Reader',
    };
    this.selectedUser.set(user);
    this.searchControl.setValue(user.displayName);
    this.selectionChange.emit(user);
  }

  emitSelection(): void {
    if (this.selectedUser()) {
      this.buttonClick.emit(this.selectedUser());
      this.searchControl.reset();
      this.selectedUser.set(null);
    }
  }
}
